import { intersection, flatten } from 'lodash';
import type { Service } from 'Components/widgets/home-details/HomeDetails';
import 'Components/widgets/home-details/HomeDetails';
import { t } from 'Src/i18n';
import { formatAmount } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import type { AttendeesType } from 'Store/booking/attendees/attendees.selector';
import 'Store/booking/attendees/attendees.selector';
import { getAttendeeNamePlaceholder } from 'Utils/participants/participants.utils';
export type HouseholdMember = {
  services: Array<Service>;
  total: FormattedPrice;
  id: string;
  name: string;
};
export type MemberHousehold = {
  adults: Array<HouseholdAttendee>;
  children: Array<HouseholdAttendee>;
  all: Array<HouseholdAttendee>;
  key: number;
};
export type Household = {
  adults: Array<HouseholdMember>;
  children: Array<HouseholdMember>;
  key: number;
};
export type AttendeeLineItem = {
  type: string;
  offerId?: string;
  price: Price;
};

export type PriceItem = AttendeeLineItem;

function getAccommodation(proposal: Proposal, attendeePrice: AttendeePrice): Accommodation {
  let [accommodation] = proposal.accommodations;

  if (proposal.accommodations.length > 1) {
    accommodation = proposal.accommodations.find((acc) => {
      return acc.attendeesDistribution.attendees.find((accAtt) => accAtt.id === attendeePrice.id);
    });
  }

  return accommodation;
}

export const getRooms = (attendeePrice: AttendeePrice, proposal: Proposal): Array<Service> => {
  const priceItems = attendeePrice.priceItems as unknown as AttendeeLineItem[];
  return priceItems
    .filter((item) => item.type === 'ROOM')
    .map((roomItem) => {
      const accommodation = getAccommodation(proposal, attendeePrice);
      const { value, formattedValue } = roomItem.price;
      return {
        title: accommodation.label,
        price: {
          formattedValue,
          value,
        },
      };
    });
};
export const getServices = (
  attendeePrice: AttendeePrice,
  proposal: Proposal,
  product: Product,
): Array<Service> => {
  return proposal.additionalServices.activities
    .filter(
      (act) =>
        act.pricingInfo.isPartiallyRented &&
        act.pricingInfo.attendees.some(
          // all the attendees may be present but only the one who actually rented it has a rentedAmount
          (att) => att.id === attendeePrice.id && att.rentedAmount.value !== 0,
        ),
    )
    .map((act: AdditionalServiceWithScheduleType) => {
      const allActivities: Array<ProductActivityCategoryType> = flatten(
        Object.values(product.activityCategories),
      );
      const category: ProductActivityCategoryType | null | undefined = allActivities.find(
        ({ subCategories }) => {
          return (
            intersection(
              subCategories.map(({ id }) => id),
              act.productInfo.parentActivityCategoriesId,
            ).length > 0
          );
        },
      );
      const price = act.pricingInfo.attendees.find((att) => att.id === attendeePrice.id) as {
        rentedAmount: Price;
      };
      return {
        title: category?.subCategories?.[0]?.label || '',
        description: act.productInfo.label,
        price: price.rentedAmount,
      };
    });
};
export const getDiscounts = (attendeePrice: AttendeePrice, proposal: Proposal): Array<Service> => {
  const priceItems = attendeePrice.priceItems as unknown as AttendeeLineItem[];
  return priceItems
    .filter((item) => item.type === 'OFFER')
    .map((offerItem) => {
      const proposalDiscount: ProposalDiscount = proposal.price.discounts.find(
        (discount) => discount.offerId === offerItem.offerId,
      );
      return {
        title: proposalDiscount.offerName,
        description: '',
        price: offerItem.price,
      };
    });
};
const feeTypes = ['SUBSCRIPTION', 'TOURISTIC_TAXES', 'PORT_TAXES'];

const isFee = (priceItem) => feeTypes.includes(priceItem.type);

export const getFees = (attendeePrice: AttendeePrice): Array<Service> => {
  const priceItems = attendeePrice.priceItems as unknown as AttendeeLineItem[];
  return priceItems.filter(isFee).map((priceItem) => ({
    title: t(`booking.summary.priceDetails.fee.${priceItem.type.toLowerCase()}`),
    description: '',
    price: priceItem.price,
  }));
};
export const getTransfers = (attendeePrice: AttendeePrice): Array<Service> => {
  const priceItems = attendeePrice.priceItems as unknown as AttendeeLineItem[];
  return priceItems
    .filter((item) => item.type === 'TRANSFER')
    .map((priceItem) => ({
      title: t('booking.summary.priceDetails.transfer'),
      description: '',
      price: priceItem.price,
    }));
};
export const getRentals = (attendeePrice: AttendeePrice, proposal: Proposal): Array<Service> => {
  return proposal.additionalServices.rentals
    .filter((rental) => {
      return (
        rental.pricingInfo.isPartiallyRented &&
        rental.pricingInfo.attendees.some(
          (att) => att.id === attendeePrice.id && att.rentedAmount.value !== 0,
        )
      );
    })
    .map((rental) => {
      const price: AttendeeWithSchedulesType = rental.pricingInfo.attendees.find(
        (att) => att.id === attendeePrice.id,
      );
      return {
        title: rental.productInfo.label,
        description: '',
        price: { ...price.rentedAmount },
      };
    });
};
export const getOthers = (attendeePrice: AttendeePrice): Array<Service> => {
  const isOtherType = (item: AttendeeLineItem) =>
    ['INSURANCE', 'VISAS', 'TAX', 'TOURIST_TAX', 'OTHERS'].includes(item.type);

  const priceItems = attendeePrice.priceItems as unknown as AttendeeLineItem[];
  return priceItems.filter(isOtherType).map((priceItem) => ({
    title: t(`booking.summary.priceDetails.other.${priceItem.type.toLowerCase()}`),
    description: '',
    price: priceItem.price,
  }));
};
export const getTotalPrice = (household: Household, appContext: AppContext) => {
  if (!household.adults.length && !household.children.length) {
    return {
      value: 0,
      formattedValue: '0',
    };
  }

  const currency = (household.adults[0] || household.children?.[0])?.total?.currency || '';
  const householdTotal: number = household.adults
    .concat(household.children || [])
    .reduce((total: number, current: HouseholdMember): number => total + current.total.value, 0);
  return {
    value: householdTotal,
    formattedValue: formatAmount(householdTotal, currency, appContext.locale),
  };
};

const mapClient = (
  proposalAttendee: AdultAttendeeWithInfosType | ChildAttendeeWithInfosType,
  proposal: Proposal,
  resort: Product | null | undefined,
  attendeePrice: AttendeePrice | null | undefined,
): HouseholdMember => {
  const { attendees } = proposal;
  const attendeeLabel = getAttendeeNamePlaceholder(
    proposalAttendee,
    !!attendees.children.find(({ id }) => id === proposalAttendee.id),
    proposalAttendee.id === 'A',
  );
  return {
    name: attendeeLabel,
    services:
      attendeePrice && resort
        ? [
            ...getRooms(attendeePrice, proposal),
            ...getServices(attendeePrice, proposal, resort),
            ...getRentals(attendeePrice, proposal),
            ...getFees(attendeePrice),
            ...getTransfers(attendeePrice),
            ...getDiscounts(attendeePrice, proposal),
            ...getOthers(attendeePrice),
          ]
        : [],
    total: attendeePrice?.total,
    id: proposalAttendee.id,
  };
};

export const addAttendeesPriceToAttendees = (
  households: Array<
    AttendeesType & {
      key: number;
    }
  >,
  proposal: Proposal,
  product: Product,
): Array<Household> => {
  const newHouseholds = [];

  const getAttendeePrice = (attendee) =>
    proposal.attendeesPrice.find((attPr) => attendee?.id === attPr.id);

  households.forEach(({ adults, children }, index) => {
    newHouseholds.push({
      key: index,
      adults: adults.map((adult) => mapClient(adult, proposal, product, getAttendeePrice(adult))),
      children: children?.map((child) =>
        mapClient(child, proposal, product, getAttendeePrice(child)),
      ),
    });
  });
  return newHouseholds;
};
